"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Consultation() {
  const prefersReducedMotion = useReducedMotion();
  const headlineWords = [{
    text: "Ready",
    italic: false
  }, {
    text: "to",
    italic: false
  }, {
    text: "scale",
    italic: false
  }, {
    text: "your",
    italic: false
  }, {
    text: "search",
    italic: true
  }, {
    text: "visibility",
    italic: false
  }, {
    text: "with",
    italic: false
  }, {
    text: "me?",
    italic: false
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.06,
        delayChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const wordVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.9,
        ease: [0.2, 0.7, 0.2, 1] as [number, number, number, number]
      }
    }
  };
  const fadeUpVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.6,
        ease: [0.2, 0.7, 0.2, 1] as [number, number, number, number]
      }
    }
  };
  const kickerVariants = {
    hidden: {
      opacity: 0,
      x: prefersReducedMotion ? 0 : -8
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.4,
        ease: [0.2, 0.7, 0.2, 1] as [number, number, number, number]
      }
    }
  };
  const markerPathVariants = {
    hidden: {
      pathLength: 0,
      opacity: 0
    },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          duration: prefersReducedMotion ? 0 : 0.9,
          ease: "easeOut" as const
        },
        opacity: {
          duration: 0.2
        }
      }
    }
  };
  return <section data-section-id="30692" id="consultationcta" data-nav="light" className="relative bg-background text-foreground py-40 md:py-56 overflow-hidden">
      {}
      <div className="flex justify-center mb-16 md:mb-24">
        <motion.svg width="220" height="18" viewBox="0 0 220 18" fill="none" xmlns="http://www.w3.org/2000/svg" initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.5
      }} aria-hidden="true">
          <motion.path d="M4 10 C 40 4, 80 14, 120 8 S 200 6, 216 11" stroke="hsl(340 75% 65%)" strokeWidth="5" strokeLinecap="round" fill="none" variants={markerPathVariants} />
        </motion.svg>
      </div>

      <div className="mx-auto max-w-[1100px] px-5 md:px-16 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }}>
          {}
          <motion.div variants={kickerVariants} className="flex flex-col items-center gap-4 mb-10 md:mb-14">
            <svg width="24" height="6" viewBox="0 0 24 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M2 3 C 6 1, 12 5, 22 2" stroke="hsl(340 75% 65%)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            </svg>
            <span className="font-sans text-[14px] font-medium uppercase leading-none text-muted-foreground" style={{
            letterSpacing: "0.18em"
          }}>
              Book Your Strategy Session
            </span>
          </motion.div>

          {}
          <motion.h2 variants={containerVariants} className="font-serif font-normal text-foreground mx-auto max-w-[18ch]" style={{
          fontSize: "clamp(36px, 4.5vw, 58px)",
          lineHeight: 0.96,
          letterSpacing: "-0.028em"
        }}>
            {headlineWords.map((word, i) => <motion.span key={i} data-index={i} variants={wordVariants} className="inline-block mr-[0.25em]">
                {word.italic ? <span className="italic">{word.text}</span> : word.text}
              </motion.span>)}
          </motion.h2>

          {}
          <motion.div variants={fadeUpVariants} className="mt-24 md:mt-24 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" style={{
          marginTop: "clamp(56px, 8vw, 96px)"
        }}>
            <Button asChild className="group h-auto rounded-lg bg-primary text-primary-foreground px-8 py-[18px] text-[17px] font-medium hover:bg-primary/90 shadow-sm w-full sm:w-auto" style={{
            letterSpacing: "0.02em"
          }}>
              <Link to="/ContactPage">
                <span>Book a free consultation</span>
                <ArrowRight className="ml-1 size-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </Button>

            <Button asChild variant="outline" className="h-auto rounded-lg border border-foreground bg-transparent text-foreground px-8 py-[18px] text-[17px] font-medium hover:bg-foreground hover:text-background shadow-sm w-full sm:w-auto transition-colors duration-200" style={{
            letterSpacing: "0.02em"
          }}>
              <Link to="/CaseStudiesPage">
                <span>Explore my case studies &rarr;</span>
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}